import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CkControlsModule } from '@ck-core/ck-controls';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs';

import { mapLanguageEnumToLangId } from './core/i18n/i18n.config';
import { SiteSettingsStore } from './core/services/site-settings.store';
import { ViewportService } from './core/services/viewport.service';
import { Header } from './shared/components/header/header';
import { MobileUnsupportedComponent } from './shared/components/mobile-unsupported/mobile-unsupported.component';
import { RouteProgressComponent } from './shared/route-progress/route-progress.component';
import { UnsavedChangesService } from './shared/unsaved-changes/unsaved-changes.service';
import { CKAuthService } from '@ck-core/ck-auth';
import { environment } from '../environments/environment';

@Component({
    selector: 'app-root',
    imports: [
        CommonModule,
        RouterOutlet,
        Header,
        CkControlsModule,
        RouteProgressComponent,
        MobileUnsupportedComponent,
    ],
    templateUrl: './app.html',
    styleUrl: './app.scss',
})
export class App {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly unsavedChanges = inject(UnsavedChangesService);
    private readonly siteSettings = inject(SiteSettingsStore);
    private readonly transloco = inject(TranslocoService);
    private readonly viewport = inject(ViewportService);
    private readonly title = inject(Title);

    protected readonly chromeless = signal(false);
    protected readonly isMobile = this.viewport.isMobile;

    constructor(public ckAuthSvc: CKAuthService) {
        void this.unsavedChanges;

        // Keep transloco's active language in sync with the Global Settings
        // language. Runs on app boot (from the cached SiteSettings) and again
        // any time the user saves a new language in Global Settings.
        effect(() => {
            const langId = mapLanguageEnumToLangId(this.siteSettings.language());
            if (this.transloco.getActiveLang() !== langId) {
                this.transloco.setActiveLang(langId);
            }
        });

        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            let snapshot = this.route.snapshot;
            let title = snapshot.data?.['title'];
            while (snapshot.firstChild) {
                snapshot = snapshot.firstChild;
                if (snapshot.data?.['title']) {
                    title = snapshot.data['title'];
                }
            }
            this.chromeless.set(snapshot.data?.['layout'] === 'none');

            if (title) {
                this.title.setTitle(`${title} | ${environment.projectName}`);
            } else {
                this.title.setTitle(`${environment.projectName}`);
            }
        });
    }

    public get isMimic(): boolean {
        return this.ckAuthSvc.authData.moreInfo.isMimic ?? false;
    }
}
